import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Package, Plus, Minus, Sparkles, CheckCircle2, DollarSign, Clock, X, Wallet,
} from "lucide-react";
import { PILLARS, US_STATES } from "@/data/pillars";
import { api } from "@/lib/api";
import { toast } from "sonner";

const HOME_HEALTH = PILLARS.find((p) => p.key === "home-health");
const SERVICES = HOME_HEALTH.services;
const PACKAGES = HOME_HEALTH.packages || [];

const MIN_SERVICES = 3; // families must pick at least 3 services

const CREDENTIALS = [
    { value: "HHA", label: "Home Health Aide (HHA)" },
    { value: "CNA", label: "Certified Nursing Assistant (CNA)" },
    { value: "LPN", label: "Licensed Practical Nurse (LPN)" },
    { value: "RN", label: "Registered Nurse (RN)" },
];

export const HH_BOOKING_TIDS = {
    root: "hh-booking-root",
    modeAlaCarte: "hh-booking-mode-a-la-carte",
    modePackage: "hh-booking-mode-package",
    packageCard: "hh-booking-package-card",
    serviceRow: "hh-booking-service-row",
    serviceToggle: "hh-booking-service-toggle",
    serviceHoursInput: "hh-booking-service-hours-input",
    serviceHoursPerDay: "hh-booking-service-hours-per-day",
    serviceDaysPerWeek: "hh-booking-service-days-per-week",
    stateSelect: "hh-booking-state-select",
    credentialSelect: "hh-booking-credential-select",
    notesInput: "hh-booking-notes-input",
    submitBtn: "hh-booking-submit-btn",
    weeklyTotal: "hh-booking-weekly-total",
    totalHours: "hh-booking-total-hours",
    minServicesNotice: "hh-booking-min-services-notice",
};

/**
 * Multi-service home-health booking form.
 * - Mode A ("Package"): pick a bundle, hours + services pre-filled, family may bump hours or add extras.
 * - Mode B ("À la carte"): checkbox each service, set hours per week, live running total.
 */
export default function HomeHealthBookingForm({
    initialState = "",
    initialServiceId = "",
    onSuccess,
}) {
    const [mode, setMode] = useState("package"); // "package" | "alacarte"
    const [packageId, setPackageId] = useState(null);
    // Line items keyed by service_id.
    // Weekly: { hours: N, enabled }
    // Daily : { hoursPerDay: N, daysPerWeek: D, enabled } — hours (weekly total) is derived.
    const [lines, setLines] = useState(() => {
        const seed = {};
        SERVICES.forEach((s) => {
            seed[s.id] = s.cadence === "daily"
                ? { hoursPerDay: 0, daysPerWeek: s.defaultDaysPerWeek || 7, enabled: false }
                : { hours: 0, enabled: false };
        });
        return seed;
    });
    const [state, setState] = useState(initialState);
    const [credentials, setCredentials] = useState([]);
    const [notes, setNotes] = useState("");
    const [busy, setBusy] = useState(false);

    // Keep state field in sync when initialState prop hydrates (e.g. after /me loads)
    useEffect(() => {
        if (initialState && !state) setState(initialState);
    }, [initialState, state]);

    // Prefill from signup context
    useEffect(() => {
        if (!initialServiceId) return;
        const svc = SERVICES.find((s) => s.id === initialServiceId);
        if (!svc) return;
        setLines((prev) => ({
            ...prev,
            [initialServiceId]: svc.cadence === "daily"
                ? {
                    hoursPerDay: svc.defaultHoursPerDay || Math.max(6, svc.minHoursPerDay || 6),
                    daysPerWeek: svc.defaultDaysPerWeek || 7,
                    enabled: true,
                }
                : { hours: 20, enabled: true },
        }));
        setMode("alacarte");
    }, [initialServiceId]); // lines intentionally omitted — seed only once

    function applyPackage(pkgId) {
        const pkg = PACKAGES.find((p) => p.id === pkgId);
        if (!pkg) return;
        const weeklyPerService = Math.max(1, Math.round(pkg.baseHours / pkg.serviceIds.length));
        const next = {};
        SERVICES.forEach((s) => {
            const inPkg = pkg.serviceIds.includes(s.id);
            if (s.cadence === "daily") {
                if (inPkg) {
                    const minPerDay = Math.max(6, s.minHoursPerDay || 6);
                    const perDay = Math.max(minPerDay, Math.round(weeklyPerService / 7));
                    next[s.id] = { hoursPerDay: perDay, daysPerWeek: 7, enabled: true };
                } else {
                    next[s.id] = { hoursPerDay: 0, daysPerWeek: s.defaultDaysPerWeek || 7, enabled: false };
                }
            } else {
                next[s.id] = inPkg
                    ? { hours: weeklyPerService, enabled: true }
                    : { hours: 0, enabled: false };
            }
        });
        setLines(next);
        setPackageId(pkgId);
        if (pkg.recommendedCredential) {
            setCredentials((prev) => (prev.includes(pkg.recommendedCredential) ? prev : [...prev, pkg.recommendedCredential]));
        }
    }

    function toggleService(id) {
        const svc = SERVICES.find((s) => s.id === id);
        if (!svc) return;
        setLines((prev) => {
            const cur = prev[id];
            const enabled = !cur.enabled;
            if (svc.cadence === "daily") {
                const minPerDay = Math.max(6, svc.minHoursPerDay || 6);
                return {
                    ...prev,
                    [id]: {
                        hoursPerDay: enabled
                            ? Math.max(cur.hoursPerDay || 0, svc.defaultHoursPerDay || minPerDay)
                            : 0,
                        daysPerWeek: cur.daysPerWeek || svc.defaultDaysPerWeek || 7,
                        enabled,
                    },
                };
            }
            return {
                ...prev,
                [id]: { hours: enabled ? Math.max(cur.hours, 5) : 0, enabled },
            };
        });
        setPackageId(null);
    }

    function bumpHours(id, delta) {
        setLines((prev) => {
            const cur = prev[id];
            const next = Math.min(168, Math.max(0, (cur.hours || 0) + delta));
            return {
                ...prev,
                [id]: { ...cur, hours: next, enabled: next > 0 },
            };
        });
        setPackageId(null);
    }

    function setHours(id, value) {
        const svc = SERVICES.find((s) => s.id === id);
        if (!svc) return;
        if (svc.cadence === "daily") {
            // For daily services, "clearing" via the summary chip means disabling.
            setLines((prev) => ({
                ...prev,
                [id]: { ...prev[id], hoursPerDay: 0, enabled: false },
            }));
        } else {
            const n = Math.min(168, Math.max(0, parseInt(value, 10) || 0));
            setLines((prev) => ({
                ...prev,
                [id]: { hours: n, enabled: n > 0 },
            }));
        }
        setPackageId(null);
    }

    function setHoursPerDay(id, value) {
        const svc = SERVICES.find((s) => s.id === id);
        if (!svc) return;
        const minPerDay = Math.max(6, svc.minHoursPerDay || 6);
        const raw = parseInt(value, 10);
        if (Number.isNaN(raw) || raw === 0) {
            setLines((prev) => ({
                ...prev,
                [id]: { ...prev[id], hoursPerDay: 0, enabled: false },
            }));
        } else {
            const n = Math.min(24, Math.max(minPerDay, raw));
            setLines((prev) => ({
                ...prev,
                [id]: { ...prev[id], hoursPerDay: n, enabled: true },
            }));
        }
        setPackageId(null);
    }

    function setDaysPerWeek(id, value) {
        const n = Math.min(7, Math.max(1, parseInt(value, 10) || 1));
        setLines((prev) => ({
            ...prev,
            [id]: { ...prev[id], daysPerWeek: n },
        }));
        setPackageId(null);
    }

    const selectedLines = useMemo(() => {
        return SERVICES
            .filter((s) => {
                const l = lines[s.id];
                if (!l) return false;
                if (s.cadence === "daily") return (l.hoursPerDay || 0) > 0 && (l.daysPerWeek || 0) > 0;
                return (l.hours || 0) > 0;
            })
            .map((s) => {
                const l = lines[s.id];
                if (s.cadence === "daily") {
                    const hpd = l.hoursPerDay;
                    const dpw = l.daysPerWeek;
                    return {
                        service_id: s.id,
                        service_label: s.label,
                        cadence: "daily",
                        hours_per_day: hpd,
                        days_per_week: dpw,
                        hours_per_week: Math.min(168, hpd * dpw),
                        hourly_rate_cents: s.hourlyRateCents || 2800,
                    };
                }
                return {
                    service_id: s.id,
                    service_label: s.label,
                    cadence: "weekly",
                    hours_per_week: l.hours,
                    hourly_rate_cents: s.hourlyRateCents || 2800,
                };
            });
    }, [lines]);

    const totalHours = selectedLines.reduce((n, l) => n + l.hours_per_week, 0);
    const weeklyTotalCents = selectedLines.reduce(
        (n, l) => n + l.hours_per_week * l.hourly_rate_cents, 0
    );
    const blendedHourly = totalHours ? weeklyTotalCents / totalHours / 100 : 0;

    // If a package is picked and lines match, use package weekly price for display
    const activePackage = packageId ? PACKAGES.find((p) => p.id === packageId) : null;

    async function submit(e) {
        e.preventDefault();
        if (selectedLines.length === 0) return toast.error("Add hours to at least one service");
        if (selectedLines.length < MIN_SERVICES) {
            return toast.error(`Please pick at least ${MIN_SERVICES} services (you've picked ${selectedLines.length}).`);
        }
        if (!state) return toast.error("Pick a state");
        setBusy(true);
        try {
            await api.post("/care/requests", {
                services: selectedLines,
                package_id: packageId,
                package_label: activePackage?.name || null,
                state,
                required_credential: credentials[0] || null,
                required_credentials: credentials,
                notes: notes || null,
            });
            toast.success("Care request posted");
            setNotes("");
            onSuccess?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed");
        } finally {
            setBusy(false);
        }
    }

    return (
        <div data-testid={HH_BOOKING_TIDS.root} className="space-y-6">
            {/* Private-pay banner */}
            <div className="rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-3 flex items-start gap-2">
                <Wallet className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
                <div className="text-xs font-dmsans text-amber-900 leading-relaxed">
                    <span className="font-outfit font-semibold">Private pay only.</span>{" "}
                    We don&rsquo;t bill Medicare, Medicaid, or private insurance for Home Health. You pay per hour or per package via the card on file.
                </div>
            </div>

            {/* Mode toggle */}
            <div className="inline-flex rounded-full bg-stone-100 p-1">
                <button
                    type="button"
                    data-testid={HH_BOOKING_TIDS.modePackage}
                    onClick={() => setMode("package")}
                    className={`rounded-full px-5 h-9 text-sm font-outfit font-medium transition-all ${
                        mode === "package"
                            ? "bg-white text-stone-900 shadow-sm"
                            : "text-stone-600 hover:text-stone-900"
                    }`}
                >
                    <Package className="w-4 h-4 mr-1.5 inline" /> Packages
                </button>
                <button
                    type="button"
                    data-testid={HH_BOOKING_TIDS.modeAlaCarte}
                    onClick={() => setMode("alacarte")}
                    className={`rounded-full px-5 h-9 text-sm font-outfit font-medium transition-all ${
                        mode === "alacarte"
                            ? "bg-white text-stone-900 shadow-sm"
                            : "text-stone-600 hover:text-stone-900"
                    }`}
                >
                    <Sparkles className="w-4 h-4 mr-1.5 inline" /> À la carte
                </button>
            </div>

            {/* Package cards */}
            {mode === "package" && (
                <div className="grid md:grid-cols-3 gap-4">
                    {PACKAGES.map((pkg) => {
                        const active = packageId === pkg.id;
                        return (
                            <button
                                key={pkg.id}
                                type="button"
                                data-testid={HH_BOOKING_TIDS.packageCard}
                                data-pkg={pkg.id}
                                data-active={active}
                                onClick={() => applyPackage(pkg.id)}
                                className={`text-left rounded-2xl p-6 border-2 transition-all ${
                                    active
                                        ? "border-emerald-600 bg-emerald-50 shadow-md"
                                        : "border-stone-200 bg-white hover:border-emerald-300 hover:shadow-sm"
                                } ${pkg.featured && !active ? "ring-1 ring-emerald-300" : ""}`}
                            >
                                {pkg.featured && (
                                    <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-emerald-700 mb-2">
                                        Most popular
                                    </div>
                                )}
                                <div className="font-outfit text-xl font-semibold text-stone-900">
                                    {pkg.name}
                                </div>
                                <div className="text-sm text-stone-500 mt-1 font-dmsans">
                                    {pkg.tagline}
                                </div>
                                <div className="mt-4 flex items-baseline gap-1">
                                    <span className="font-outfit text-3xl font-semibold text-stone-900">
                                        ${(pkg.weeklyPriceCents / 100).toFixed(0)}
                                    </span>
                                    <span className="text-xs text-stone-500 font-dmsans">/ week</span>
                                </div>
                                <div className="text-xs text-stone-500 font-dmsans mt-1">
                                    {pkg.baseHours} hours · {pkg.serviceIds.length} services
                                </div>
                                <ul className="mt-4 space-y-1.5">
                                    {pkg.highlights.map((h) => (
                                        <li key={h} className="flex gap-2 text-xs text-stone-700 font-dmsans">
                                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" />
                                            {h}
                                        </li>
                                    ))}
                                </ul>
                                <div className={`mt-5 rounded-full text-center h-10 flex items-center justify-center text-sm font-outfit font-semibold ${
                                    active
                                        ? "bg-emerald-700 text-white"
                                        : "bg-stone-100 text-stone-800"
                                }`}>
                                    {active ? "Selected" : "Pick this package"}
                                </div>
                            </button>
                        );
                    })}
                </div>
            )}

            {/* À la carte grid */}
            {mode === "alacarte" && (
                <div className="rounded-2xl border border-stone-200 bg-white overflow-hidden">
                    <div className="px-5 py-3 bg-stone-50 border-b border-stone-200 text-xs font-bold uppercase tracking-[0.2em] text-stone-500 grid grid-cols-12 gap-4">
                        <div className="col-span-6">Service</div>
                        <div className="col-span-2 text-right">Rate</div>
                        <div className="col-span-4 text-center">Schedule</div>
                    </div>
                    {SERVICES.map((s) => {
                        const line = lines[s.id];
                        const isDaily = s.cadence === "daily";
                        const on = isDaily
                            ? (line.enabled && line.hoursPerDay > 0)
                            : (line.enabled || line.hours > 0);
                        const minPerDay = Math.max(6, s.minHoursPerDay || 6);
                        return (
                            <div
                                key={s.id}
                                data-testid={HH_BOOKING_TIDS.serviceRow}
                                data-service={s.id}
                                data-cadence={s.cadence}
                                className={`px-5 py-4 grid grid-cols-12 gap-4 items-center border-b border-stone-100 last:border-b-0 transition-colors ${
                                    on ? "bg-emerald-50/40" : "bg-white"
                                }`}
                            >
                                <button
                                    type="button"
                                    data-testid={HH_BOOKING_TIDS.serviceToggle}
                                    data-service={s.id}
                                    data-active={on}
                                    onClick={() => toggleService(s.id)}
                                    className="col-span-6 flex items-center gap-3 text-left"
                                >
                                    <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${
                                        on
                                            ? "bg-emerald-600 border-emerald-600 text-white"
                                            : "border-stone-300 bg-white"
                                    }`}>
                                        {on && <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2.5} />}
                                    </div>
                                    <div className="min-w-0">
                                        <div className="font-outfit text-sm font-semibold text-stone-900 flex items-center gap-2 flex-wrap">
                                            {s.label}
                                            {isDaily && (
                                                <span className="inline-flex items-center gap-1 rounded-full bg-indigo-100 text-indigo-800 text-[10px] font-medium px-2 py-0.5 font-dmsans">
                                                    Daily · min {minPerDay} h/day
                                                </span>
                                            )}
                                        </div>
                                        {s.credentialHint && (
                                            <div className="text-[10px] text-stone-500 font-dmsans">
                                                Best fit: {s.credentialHint}
                                            </div>
                                        )}
                                    </div>
                                </button>
                                <div className="col-span-2 text-right">
                                    <div className="font-outfit text-sm font-semibold text-stone-900">
                                        ${((s.hourlyRateCents || 0) / 100).toFixed(0)}
                                    </div>
                                    <div className="text-[10px] text-stone-500 font-dmsans">/hr</div>
                                </div>
                                {isDaily ? (
                                    <div className="col-span-4 flex items-center justify-center gap-2 flex-wrap">
                                        <div className="flex items-center gap-1">
                                            <Input
                                                data-testid={HH_BOOKING_TIDS.serviceHoursPerDay}
                                                data-service={s.id}
                                                type="number"
                                                min={minPerDay}
                                                max={24}
                                                value={line.hoursPerDay || ""}
                                                placeholder={String(s.defaultHoursPerDay || minPerDay)}
                                                onChange={(e) => setHoursPerDay(s.id, e.target.value)}
                                                className="h-8 w-16 rounded-lg text-center text-sm px-1"
                                            />
                                            <span className="text-[10px] text-stone-500 font-dmsans whitespace-nowrap">h/day</span>
                                        </div>
                                        <div className="flex items-center gap-0.5" role="group" aria-label="Days per week">
                                            {[1, 2, 3, 4, 5, 6, 7].map((d) => {
                                                const activePill = (line.daysPerWeek || 0) === d;
                                                return (
                                                    <button
                                                        key={d}
                                                        type="button"
                                                        data-testid={HH_BOOKING_TIDS.serviceDaysPerWeek}
                                                        data-service={s.id}
                                                        data-days={d}
                                                        onClick={() => setDaysPerWeek(s.id, d)}
                                                        disabled={!on}
                                                        className={`h-7 w-7 text-[11px] font-outfit rounded-full transition-colors ${
                                                            activePill
                                                                ? "bg-stone-900 text-white"
                                                                : on
                                                                    ? "bg-stone-100 text-stone-700 hover:bg-stone-200"
                                                                    : "bg-stone-50 text-stone-400 cursor-not-allowed"
                                                        }`}
                                                    >{d}</button>
                                                );
                                            })}
                                            <span className="ml-1 text-[10px] text-stone-500 font-dmsans">days/wk</span>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="col-span-4 flex items-center justify-center gap-1">
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => bumpHours(s.id, -1)}
                                            disabled={line.hours <= 0}
                                            className="h-8 w-8 rounded-full border border-stone-200"
                                        >
                                            <Minus className="w-3.5 h-3.5" />
                                        </Button>
                                        <Input
                                            data-testid={HH_BOOKING_TIDS.serviceHoursInput}
                                            data-service={s.id}
                                            type="number"
                                            min={0}
                                            max={168}
                                            value={line.hours}
                                            onChange={(e) => setHours(s.id, e.target.value)}
                                            className="h-8 w-14 rounded-lg text-center text-sm px-1"
                                        />
                                        <span className="text-[10px] text-stone-500 font-dmsans whitespace-nowrap">h/wk</span>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => bumpHours(s.id, 1)}
                                            className="h-8 w-8 rounded-full border border-stone-200"
                                        >
                                            <Plus className="w-3.5 h-3.5" />
                                        </Button>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Package add-ons (visible when a package is picked) */}
            {mode === "package" && packageId && (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-5">
                    <div className="flex items-center gap-2 mb-3">
                        <Sparkles className="w-4 h-4 text-emerald-700" />
                        <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-700">
                            Package selected
                        </div>
                    </div>
                    <div className="text-sm text-stone-700 font-dmsans">
                        Prefer to tweak? Switch to <button
                            type="button"
                            onClick={() => setMode("alacarte")}
                            className="text-emerald-700 font-medium underline"
                        >
                            À la carte
                        </button> and adjust hours per service.
                    </div>
                </div>
            )}

            {/* Selection summary */}
            {selectedLines.length > 0 && (
                <Card className="rounded-2xl border-stone-200 bg-stone-50/60">
                    <CardContent className="p-5">
                        <div className="flex items-start justify-between gap-4 flex-wrap">
                            <div>
                                <div className="text-xs font-bold uppercase tracking-[0.2em] text-stone-500 mb-2">
                                    Selected services
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                    {selectedLines.map((l) => (
                                        <Badge
                                            key={l.service_id}
                                            className="bg-white border border-stone-200 text-stone-800 hover:bg-white font-dmsans"
                                        >
                                            {l.service_label} · {l.hours_per_week}h
                                            <button
                                                type="button"
                                                onClick={() => setHours(l.service_id, 0)}
                                                className="ml-1.5 text-stone-400 hover:text-red-600"
                                                aria-label="Remove"
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="text-xs font-bold uppercase tracking-[0.2em] text-stone-500 mb-1">
                                    Estimated weekly total
                                </div>
                                <div
                                    data-testid={HH_BOOKING_TIDS.weeklyTotal}
                                    className="font-outfit text-3xl font-semibold text-emerald-800"
                                >
                                    ${(weeklyTotalCents / 100).toFixed(0)}
                                </div>
                                <div className="text-xs text-stone-500 font-dmsans">
                                    <span data-testid={HH_BOOKING_TIDS.totalHours}>{totalHours}h/wk</span>
                                    {" · "}
                                    ${blendedHourly.toFixed(2)} blended /hr
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            <form onSubmit={submit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                        <Label>State</Label>
                        <Select value={state} onValueChange={setState}>
                            <SelectTrigger
                                data-testid={HH_BOOKING_TIDS.stateSelect}
                                className="h-11 rounded-xl mt-1.5"
                            >
                                <SelectValue placeholder="Pick a state" />
                            </SelectTrigger>
                            <SelectContent className="max-h-80">
                                {US_STATES.map(([code, name]) => (
                                    <SelectItem key={code} value={code}>{name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>Preferred credentials <span className="text-stone-500 font-normal">(pick any that will work)</span></Label>
                        <div
                            data-testid={HH_BOOKING_TIDS.credentialSelect}
                            className="mt-1.5 grid grid-cols-2 gap-2"
                        >
                            {CREDENTIALS.map((c) => {
                                const active = credentials.includes(c.value);
                                return (
                                    <button
                                        key={c.value}
                                        type="button"
                                        data-testid={`hh-booking-credential-${c.value.toLowerCase()}`}
                                        data-active={active}
                                        aria-pressed={active}
                                        onClick={() => setCredentials((prev) => (
                                            prev.includes(c.value)
                                                ? prev.filter((x) => x !== c.value)
                                                : [...prev, c.value]
                                        ))}
                                        className="h-11 rounded-xl border-2 border-stone-200 hover:border-emerald-500 hover:bg-emerald-50 data-[active=true]:bg-emerald-700 data-[active=true]:text-white data-[active=true]:border-emerald-700 font-outfit font-semibold text-sm transition-all px-2 text-left"
                                    >
                                        <span className="block text-[11px] font-bold tracking-wide">{c.value}</span>
                                        <span className="block text-[10px] opacity-80 font-dmsans">{c.label.replace(/\s*\([A-Z]+\)$/, "")}</span>
                                    </button>
                                );
                            })}
                        </div>
                        <p className="text-[11px] text-stone-500 mt-1.5 font-dmsans">
                            {credentials.length === 0
                                ? "No preference — any qualified caregiver may match."
                                : `Matching caregivers who hold any of: ${credentials.join(" · ")}`}
                        </p>
                    </div>
                </div>

                <div>
                    <Label>Notes</Label>
                    <Textarea
                        data-testid={HH_BOOKING_TIDS.notesInput}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        className="mt-1.5 rounded-xl min-h-[80px]"
                        placeholder="Allergies, mobility needs, preferred schedule…"
                    />
                </div>

                <div className="flex flex-wrap items-center gap-3 justify-between">
                    <div className="text-xs text-stone-500 font-dmsans inline-flex items-center gap-3">
                        <span className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" /> {totalHours} h / week
                        </span>
                        <span className="flex items-center gap-1">
                            <DollarSign className="w-3.5 h-3.5" /> ${(weeklyTotalCents / 100).toFixed(0)} / week
                        </span>
                    </div>
                    <Button
                        data-testid={HH_BOOKING_TIDS.submitBtn}
                        type="submit"
                        disabled={busy || selectedLines.length === 0}
                        className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-8 h-11"
                    >
                        {busy ? "Posting…" : "Post care request"}
                    </Button>
                </div>
            </form>
        </div>
    );
}
